import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { regexValidation } from '../../../utils/cob-utils';
import { usePushdownContext } from '../../../providers/Pushdown/PushdownProvider';
import SessionStorage from '../../../utils/SessionStorage';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { transformHTMLParserLinks } from '../../../utils/rewriteLink/rewriteLink';


export interface IName {
    firstName: string;
    lastName: string;
}
export interface IFormErrors {
    first: boolean;
    last: boolean;
    email: boolean;
    term: boolean;
}

export interface ITermRates {
    text: string;
    url: string;
}

export interface ISignUpContext {
    name: IName;
    setName: Dispatch<SetStateAction<IName>>;
    email: string;
    setEmail: Dispatch<SetStateAction<string>>;
    term: string;
    setTerm: Dispatch<SetStateAction<string>>;
    setIsTermRequired: Dispatch<SetStateAction<boolean>>;
    termRates: ITermRates[];
    setTermRates: Dispatch<SetStateAction<ITermRates[]>>;
    radio1: boolean;
    setRadio1: Dispatch<SetStateAction<boolean>>;
    radio2: boolean;
    setRadio2: Dispatch<SetStateAction<boolean>>;
    radioGroupValue: string;
    setRadioGroupValue: Dispatch<SetStateAction<string>>;
    author: boolean;
    setIsAuthor: Dispatch<SetStateAction<boolean>>;
    isSpinning: boolean;
    setIsSpinning: Dispatch<SetStateAction<boolean>>;
    isSubmitted: boolean;
    setIsSubmitted: Dispatch<SetStateAction<boolean>>;
    formErrors: IFormErrors;
    setFormErrors: Dispatch<SetStateAction<IFormErrors>>;
    apiFormID: string;
    setApiFormID: Dispatch<SetStateAction<string>>;
    campaignName: string;
    setCampaignName: Dispatch<SetStateAction<string>>;
    apiErrorCode: string;
    setApiErrorCode: Dispatch<SetStateAction<string>>;
    handleOnFocus: (type:string) => void,
    handleOnBlur: (type:string) => void,
    retrieveBulletPoints: (bulletPoint: IBulletPoint[], bulletIcon: IIcon, isLimited?: boolean, textColor?: string) => JSX.Element | undefined,
    retrieveRedirect: () => string,
    constructQuery: () => string,
    getParams: () => string,
    validateForm: () => Promise<true|false>
    validateEmail: () => Promise<any>,
    submitForm: (flag:boolean) => Promise<void>,
    grabError: () => string | undefined
}

interface IIcon {
    iconPath: string;
    iconAltText: string;
}

interface IBulletPoint {
    value: string;
}

export const SignUpContext = createContext({} as ISignUpContext);

export const useSignUpContext = () => useContext(SignUpContext);

export const SignUpContextProvider = (props:any): JSX.Element => {
    const bvKey = PathUtils.getMetaPropertyValue('briteverify');
    const [author, setIsAuthor] = useState(false);
    const [name, setName] = useState({ firstName: '', lastName: '' });
    const [email, setEmail] = useState('');
    const [term, setTerm] = useState('');
    const [isTermRequired, setIsTermRequired] = useState(false);
    const [radio1, setRadio1] = useState(false);
    const [radio2, setRadio2] = useState(false);
    const [radioGroupValue, setRadioGroupValue] = useState('');
    const [apiFormID, setApiFormID] = useState('');
    const [apiErrorCode, setApiErrorCode] = useState('');
    const [campaignName, setCampaignName] = useState('');
    const [isSpinning, setIsSpinning] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [formErrors, setFormErrors] = useState({ first: false, last: false, email: false, term: false });
    const [termRates, setTermRates] = useState([{ text: '', url: '' }]);
    const { showPushdown } = usePushdownContext();

    const handleOnFocus = (type: string) => {
        const focusedInput = document.querySelector(`.SignUp__${type}Input .SignUp__modifiedInputLabel`);
        if (focusedInput) {
            focusedInput.classList.add('SignUp__modifiedInputLabel--focused');
            focusedInput.classList.remove('SignUp__modifiedInputLabel--error');
            focusedInput.classList.remove('SignUp__modifiedInputLabel--active');
        }
    };
    const handleOnBlur = (type: string) => {
        const blurredEl = document.querySelector(`.SignUp__${type}Input .SignUp__modifiedInputLabel--focused`);
        const inputEl = document.querySelector(`.SignUp__${type}Input input`);
        if (blurredEl && inputEl) {
            blurredEl.classList.remove('SignUp__modifiedInputLabel--focused');
            if (inputEl.getAttribute('value') === '') {
                blurredEl.classList.add('SignUp__modifiedInputLabel--error');
            } else {
                blurredEl.classList.add('SignUp__modifiedInputLabel--active');
            }
        }
    };

    const retrieveBulletPoints = (bulletPoint: IBulletPoint[], bulletIcon: IIcon, isLimited?: boolean, textColor?: string) => {
        if (!bulletPoint || !bulletIcon) {
            return;
        }
        let bulletPoints = bulletPoint;
        if (isLimited) {
            bulletPoints = bulletPoint.slice(0, 3);
        }
        return (
            <>
                {
                    bulletPoints.map((item:any, index:number) => {
                        return (
                            <li key={index} className={'SignUp__bulletItemContainer'}>
                                <img className={'SignUp__bulletItemImg'}
                                    src={bulletIcon?.iconPath} loading='lazy' alt={bulletIcon?.iconAltText}/>
                                <div className={`${textColor ? `text-${textColor}`: 'SignUp__bulletItemText'}`}>
                                    {transformHTMLParserLinks(item.value)}
                                </div>
                            </li>
                        );
                    })
                }
            </>
        );
    };
    const retrieveRedirect = () => {
        let url = '';
        termRates?.forEach((item: any) => {
            if (term === item.text) {
                url = item.url;
            }
        });
        return url;
    };
    const constructQuery = () => {
        let query = `&s_first_name=${name.firstName}&s_last_name=${name.lastName}&s_email_address=${email}`;

        if (term) {
            query += `&s_term_rate=${term}`;
        }

        if (radioGroupValue) {
            query += `&s_current_cm=${radioGroupValue}`;
        }

        return query;
    };
    const getParams = () => {
        const customerId = '1154',
            formId = apiFormID;
        return `?cr=${customerId}&fm=${formId}&s_lead_gen_camp=${campaignName}${constructQuery()}`;
    };
    const validateForm = async () => {
        const emailValid = await validateEmail();
        const errors = {
            first: name.firstName === '' || !regexValidation().allow.first_name.test(name.firstName),
            last: name.lastName === '' || !regexValidation().allow.last_name.test(name.lastName),
            email: email === '' || !regexValidation().allow.email.test(email) || !emailValid,
            term: isTermRequired && term === '',
        };

        setFormErrors(errors);
        return !(Object.values(errors).includes(true));

    };
    const validateEmail = async () => {
        const onSuccess = (data: any) => {
            if (data.email.status === 'invalid' || data.email.disposable || data.email.role_address) {
                isEmailValidApi = false;
            } else {
                isEmailValidApi = true;
            }
        };

        const onError = (error: any) => {
            //Setting to true so if the API fails it doesn't prevent the user from submitting the form (From Legacy)
            console.info(error);
            isEmailValidApi = true;
        };

        const isAdobeMode = SessionStorage.contains('adobeMode');
        const useEmailApi = SessionStorage.contains('useEmailApi');
        let isEmailValidApi;

        if ((!author && !isAdobeMode) || (isAdobeMode && useEmailApi)) {
            const header = {
                'Content-Type': 'application/json',
                Authorization: `ApiKey: ${bvKey}`,
            };
            const body = { email: email };

            try {
                const response = await axios.post('https://bpi.briteverify.com/api/public/v1/fullverify',
                    body, { headers: header });
                if (response.status === 200) {
                    onSuccess(response.data);
                } else {
                    throw new Error('Error w/ Email Validation API');
                }
            } catch (error) {
                onError(error);
            }
            return isEmailValidApi;
        }
        return true;

    };
    const submitForm = async (flag: boolean) => {
        if (!flag) {
            return;
        }
        const submitRedirect = retrieveRedirect();
        const endPoint = 'https://x.creditonemail.com/ats/post.aspx',
            apiParams = getParams(),
            apiUrl = `${endPoint}${apiParams}`;

        return axios.post(apiUrl)
            .then((response) => {
                if (response.status !== 200) {
                    console.error(`Unsuccessful Response - (${response.status})`);
                }
                if (submitRedirect !== '') {
                    window.location.href = submitRedirect;
                }
            })
            .catch((error) => {
                console.error(error);
                setIsSpinning(false);
                showPushdown(apiErrorCode!);
            })
            .finally(() => setIsSubmitted(true));
    };
    const grabError = () => {
        if (formErrors.term)
            return 'Select a term.';
        return;
    };
    useEffect(() => {
        if (isSpinning) {
            validateForm()
                .then((isValid) => {
                    return submitForm(isValid);
                })
                .then(() => setIsSpinning(false));
        }
    }, [isSpinning]);
    useEffect(() => {
        const inputWrappers = document.querySelectorAll('.SignUp .Legacy-InputBase__inputWrapper, .SignUp .InputBase__inputWrapper');
        if (inputWrappers) {
            inputWrappers.forEach((wrapper) => {
                const labelText = wrapper.children[0].getAttribute('aria-label');
                const label = document.createElement('p');
                const requiredSymbol = document.createElement('span');

                label.classList.add('SignUp__modifiedInputLabel');
                requiredSymbol.classList.add('SignUp__requiredInput');

                label.innerText = labelText!;
                requiredSymbol.innerText = '*';

                label.appendChild(requiredSymbol);
                wrapper.appendChild(label);
            });
        }
    }, []);

    return (
        <SignUpContext.Provider
            value={{
                name,
                setName,
                email,
                setEmail,
                term,
                setTerm,
                setIsTermRequired,
                radio1,
                setRadio1,
                radio2,
                setRadio2,
                radioGroupValue,
                setRadioGroupValue,
                isSpinning,
                setIsSpinning,
                isSubmitted,
                setIsSubmitted,
                formErrors,
                setFormErrors,
                handleOnBlur,
                handleOnFocus,
                retrieveBulletPoints,
                retrieveRedirect,
                termRates,
                setTermRates,
                constructQuery,
                getParams,
                validateForm,
                validateEmail,
                submitForm,
                grabError,
                apiFormID,
                setApiFormID,
                campaignName,
                setCampaignName,
                apiErrorCode,
                setApiErrorCode,
                author,
                setIsAuthor,
            }}>
            {props.children}
        </SignUpContext.Provider>
    );
};